import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { DEFAULT_BRANDING, type BrandingSettings } from "@order-analyzer/shared";
import { api } from "./api.js";

/**
 * Branding/White-Label (App-Konsistenz-Audit gegen TranscriptWebapp, Thema 7): lädt die
 * Konfiguration über den unauthentifizierten `GET /api/branding`-Endpunkt (funktioniert daher auch
 * vor der Anmeldung, für die Login-Seite - siehe App.tsx#LoginGate) und wendet Markenfarbe/Titel/
 * Favicon global an. Getrennt von theme.ts (Hell-/Dunkel-Modus, rein visuell) - Branding ist die
 * Geschäftsidentität.
 */
const BrandingContext = React.createContext<BrandingSettings>(DEFAULT_BRANDING);

export function useBranding(): BrandingSettings {
  return React.useContext(BrandingContext);
}

function hexToRgbTriplet(hex: string): string {
  const match = hex.match(/^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/);
  if (!match) return "0 136 197";
  const [, r, g, b] = match;
  return `${parseInt(r, 16)} ${parseInt(g, 16)} ${parseInt(b, 16)}`;
}

function applyBranding(branding: BrandingSettings): void {
  const root = document.documentElement;
  root.style.setProperty("--color-brand", hexToRgbTriplet(branding.brandColor));
  root.style.setProperty("--color-ink", hexToRgbTriplet(branding.inkColor));
  document.title = branding.appName;

  if (branding.faviconDataUrl) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = branding.faviconDataUrl;
  }
}

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const query = useQuery({
    queryKey: ["branding"],
    queryFn: () => api.get<BrandingSettings>("/branding"),
    staleTime: 5 * 60 * 1000,
  });

  const branding = query.data ?? DEFAULT_BRANDING;

  React.useEffect(() => {
    applyBranding(branding);
  }, [branding]);

  return <BrandingContext.Provider value={branding}>{children}</BrandingContext.Provider>;
}
